import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';

export const HeroSection = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"]
  });
  
  // Parallax effect for background image
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0.3]);

  return (
    <section 
      ref={sectionRef}
      data-testid="hero-section" 
      className="relative h-screen flex items-center overflow-hidden"
    >
      {/* Full-screen Background Video with Parallax */}
      <motion.div 
        className="absolute inset-0 z-0"
        style={{ y: backgroundY }}
      >
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-[120%] object-cover"
        >
          <source src="/videos/hero-banner.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/40" />
      </motion.div>

      {/* Content */}
      <motion.div 
        className="relative z-10 w-full"
        style={{ opacity }}
      >
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <div className="max-w-5xl">
            {/* Headline with staggered word animation */}
            <motion.h1 
              className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight max-w-4xl"
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
            >
              Dokumenterbar energioptimering til erhverv
            </motion.h1>
            
            <motion.p 
              className="text-white/70 text-lg md:text-xl font-light mt-8 max-w-xl leading-relaxed"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
            >
              Vi hjælper virksomheder med at reducere energiforbruget i deres belysning gennem LED-løsninger og intelligent lysstyring — dokumenteret med konkrete før- og eftermålinger, der kan bruges i drift, økonomi og ESG-rapportering.
            </motion.p>

            {/* Proof line */}
            <motion.p 
              className="text-white/50 text-sm mt-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
            >
              20+ års erfaring · Dokumenterede energibesparelser · Driftssikre installationer
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9, ease: [0.25, 0.1, 0.25, 1] }}
              className="flex flex-wrap gap-4 mt-10"
            >
              <Link 
                to="/kontakt"
                className="inline-flex items-center gap-3 bg-white text-[#1E2A24] px-8 py-4 text-sm tracking-[0.05em] transition-all duration-300 hover:bg-[#F4F2EE] group"
                data-testid="hero-cta-book"
              >
                <span>Book gratis energigennemgang</span>
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <Link 
                to="/om-os"
                className="inline-flex items-center gap-3 bg-transparent border-2 border-white text-white px-8 py-4 text-sm tracking-[0.05em] transition-all duration-300 hover:bg-white hover:text-[#1E2A24] group"
                data-testid="hero-cta-about"
              >
                <span>Om Dilling El</span>
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};
